import React, { useState } from 'react';
import {
    Level, PhysicalUnit, BudgetItem, Task, BudgetFolder, ApuItem, ResourceItem, ProjectInfo
} from '../src/types';
import { isMatrixValueApplies, cleanTaskName, getTaskLeafChildren, findTaskByIdInTree, syncMatrixPlanningToQuality } from '../src/utils/planningUtils';
import { GraphicProgressConfig } from '../src/components/graphic-progress/GraphicProgressConfig';
interface EDTScreenProps {
    tasks: Task[];
    setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
    levels: Level[];
    setLevels: React.Dispatch<React.SetStateAction<Level[]>>;
    units: PhysicalUnit[];
    setUnits: React.Dispatch<React.SetStateAction<PhysicalUnit[]>>;
    matrixData: Record<string, string>;
    setMatrixData: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    budgetItems: BudgetItem[];
    apuData: Record<string, ApuItem[]>;
    setApuData: React.Dispatch<React.SetStateAction<Record<string, ApuItem[]>>>;
    budgetResources: ResourceItem[];
    projectInfo: ProjectInfo;
    addActivity: (activity: { type: 'planning' | 'quality' | 'payments' | 'system', title: string, description: string, user: string }) => void;
    hasPermission: (module: string, action: 'view'|'edit') => boolean;
}

const EDTScreen: React.FC<EDTScreenProps> = ({
    tasks, setTasks,
    levels, setLevels,
    units, setUnits,
    matrixData, setMatrixData,
    budgetItems,
    apuData, setApuData,
    budgetResources,
    projectInfo,
    addActivity,
    hasPermission
}) => {
    // --- Local UI State ---
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeModal, setActiveModal] = useState<'none' | 'levels' | 'units_list' | 'unit_create'>('none');
    const [activeView, setActiveView] = useState<'planning' | 'quality' | 'payment'>('planning');
    const [searchQuery, setSearchQuery] = useState('');

    // Edit Mode State
    const [isEditing, setIsEditing] = useState(false);

    // Mode Selector: Unit vs. Quantity (Cubicación)
    const [edtMode, setEdtMode] = useState<'unit' | 'quantity'>('unit');
    const [copiedValue, setCopiedValue] = useState<string>('');

    // Unlocked Parent Task Row for Inline Editing & Deploy (REGLA 2 Reformulada)
    const [unlockedParentTaskId, setUnlockedParentTaskId] = useState<string | null>(null);

    const handleDeployParentValues = (parentTask: Task) => {
        const leafSubtasks = getTaskLeafChildren(parentTask);
        const newEntries: Record<string, string> = {};

        units.forEach(u => {
            const val = matrixData[`planning_${parentTask.id}_${u.id}`] || '-';
            // Assign/deploy to ALL child subtasks in cascade
            leafSubtasks.forEach(st => {
                newEntries[`planning_${st.id}_${u.id}`] = val;
                // REGLA 3: EDT Planificación -> EDT Calidad
                newEntries[`quality_${st.id}_${u.id}`] = val;
            });
            // Ensure parent quality is updated as well
            newEntries[`quality_${parentTask.id}_${u.id}`] = val;
        });

        setMatrixData(prev => ({
            ...prev,
            ...newEntries
        }));

        addActivity({
            type: 'planning',
            title: 'Despliegue a Subtareas',
            description: `Se desplegaron las cubicaciones de la Tarea Madre "${cleanTaskName(parentTask.name)}" a ${leafSubtasks.length} subtareas.`,
            user: 'Usuario'
        });

        setUnlockedParentTaskId(null);
    };

    // Flat list of leaf tasks in the order they are rendered for vertical navigation
    const leafTasksInOrder = React.useMemo(() => {
        const getLeafTasks = (taskList: Task[]): Task[] => {
            let result: Task[] = [];
            taskList.forEach(t => {
                const isFolder = t.type === 'folder' || (t.children && t.children.length > 0);
                if (!isFolder) {
                    result.push(t);
                }
                if (t.children && t.children.length > 0) {
                    result.push(...getLeafTasks(t.children));
                }
            });
            return result;
        };
        return getLeafTasks(tasks);
    }, [tasks]);

    // Payment Details Modal State
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [editingPaymentCoords, setEditingPaymentCoords] = useState<{ taskId: string, unitId: string, taskName: string, unitName: string } | null>(null);
    const [paymentValues, setPaymentValues] = useState({ quantity: '', unitPrice: '' });
    const [cellAnexos, setCellAnexos] = useState<{ id: string, name: string, quantity: string, unitPrice: string }[]>([]);

    // APU Details Modal State
    const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
    const [apuModalOpen, setApuModalOpen] = useState(false);

    // Level Creation State
    const [isCreatingLevel, setIsCreatingLevel] = useState(false);
    const [newLevelName, setNewLevelName] = useState('');

    // Unit Creation State
    const [newUnitData, setNewUnitData] = useState({ code: '', name: '', levelId: '' });
    const [isMassiveMode, setIsMassiveMode] = useState(false);
    const [massiveData, setMassiveData] = useState({ code: '', name: '', prefix: '', quantity: '1', levelId: '' });

    // Edit Tracking State
    const [editingLevelId, setEditingLevelId] = useState<string | null>(null);
    const [filterUnitId, setFilterUnitId] = useState<string>('all');
    
    const [graphicConfigOpen, setGraphicConfigOpen] = useState(false);
    const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
    const [draggedLevelIndex, setDraggedLevelIndex] = useState<number | null>(null);
    const [lastClickedCell, setLastClickedCell] = useState<{ taskId: string, unitId: string } | null>(null);

    // Dynamic UI State
    const [taskColumnWidth, setTaskColumnWidth] = useState(300);
    const [isResizing, setIsResizing] = useState(false);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsResizing(true);
        e.preventDefault();
    };

    React.useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;
            const newWidth = Math.max(200, Math.min(600, e.clientX));
            setTaskColumnWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    const CONSTRUCTION_UNITS = ['ml', 'm2', 'm3', 'GL', 'Unidad', 'kg', 'ton', 'día', 'mes'];

    // --- Helpers ---
    const getUnitsByLevel = (levelId: string) => units.filter(u => u.levelId === levelId);

    const formatCurrency = (value: string | number) => {
        if (!value && value !== 0) return '';
        const number = typeof value === 'string' ? parseInt(value, 10) : value;
        if (isNaN(number)) return '';
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(number);
    };

    // Calculate consolidated cell value for a Parent Task across its leaf subtasks
    const getParentTaskCellValue = (task: Task, unitId: string, view = activeView) => {
        const leafChildren = getTaskLeafChildren(task);
        const targetView = view === 'payment' ? 'planning' : view;

        if (edtMode === 'quantity' || view === 'payment') {
            let sum = 0;
            let hasAny = false;
            leafChildren.forEach(leaf => {
                const val = matrixData[`${targetView}_${leaf.id}_${unitId}`];
                const num = parseFloat(val);
                if (!isNaN(num)) {
                    sum += num;
                    hasAny = true;
                }
            });
            return hasAny && sum > 0 ? sum.toString() : '-';
        } else {
            // unit mode: return '1' if parent or any leaf subtask applies ("Aplica"), otherwise '-'
            const parentVal = matrixData[`${targetView}_${task.id}_${unitId}`];
            if (isMatrixValueApplies(parentVal)) {
                return '1';
            }
            const applies = leafChildren.some(leaf => {
                const val = matrixData[`${targetView}_${leaf.id}_${unitId}`];
                return isMatrixValueApplies(val);
            });
            return applies ? '1' : '-';
        }
    };



    // Calculate Row Total (Suma horizontal consolidada sin duplicar)
    const calculateRowTotal = (taskId: string, view = activeView) => {
        const task = findTaskByIdInTree(taskId, tasks);
        if (!task) return 0;

        const isFolder = task.type === 'folder' || (task.children && task.children.length > 0);
        const targetView = view === 'payment' ? 'planning' : view;

        if (!isFolder) {
            let total = 0;
            units.forEach(u => {
                const val = matrixData[`${targetView}_${taskId}_${u.id}`];
                if (edtMode === 'quantity' || view === 'payment') {
                    const num = parseFloat(val);
                    if (!isNaN(num)) total += num;
                } else {
                    if (isMatrixValueApplies(val)) total += 1;
                }
            });
            return total;
        } else {
            // Parent Task: sum of quantities or count of units where getParentTaskCellValue is '1'
            if (edtMode === 'quantity' || view === 'payment') {
                const leafChildren = getTaskLeafChildren(task);
                let total = 0;
                leafChildren.forEach(leaf => {
                    units.forEach(u => {
                        const val = matrixData[`${targetView}_${leaf.id}_${u.id}`];
                        const num = parseFloat(val);
                        if (!isNaN(num)) total += num;
                    });
                });
                return total;
            } else {
                let total = 0;
                units.forEach(u => {
                    const parentVal = getParentTaskCellValue(task, u.id, view);
                    if (isMatrixValueApplies(parentVal)) total += 1;
                });
                return total;
            }
        }
    };

    const handleCellQuantityChange = (taskId: string, unitId: string, val: string) => {
        let cleaned = val.replace(/,/g, '.');
        cleaned = cleaned.replace(/[^0-9.]/g, '');
        const parts = cleaned.split('.');
        if (parts.length > 2) {
            cleaned = parts[0] + '.' + parts.slice(1).join('');
        }
        const finalVal = cleaned === '' ? '-' : cleaned;
        setMatrixData(prev => syncMatrixPlanningToQuality(prev, taskId, unitId, finalVal, activeView === 'planning'));
    };

    const handleCellInputClick = (taskId: string, unitId: string) => {
        if (edtMode === 'unit') {
            const key = `${activeView}_${taskId}_${unitId}`;
            const currentValue = matrixData[key] || '-';
            const applies = isMatrixValueApplies(currentValue);
            const newValue = applies ? '-' : '1';
            setMatrixData(prev => syncMatrixPlanningToQuality(prev, taskId, unitId, newValue, activeView === 'planning'));
        }
    };

    const handleCellKeyDown = (taskId: string, unitId: string, e: React.KeyboardEvent<HTMLInputElement>) => {
        const unitIndex = units.findIndex(u => u.id === unitId);
        const taskIndex = leafTasksInOrder.findIndex(t => t.id === taskId);
        
        if (unitIndex === -1 || taskIndex === -1) return;
        
        let targetTaskId = taskId;
        let targetUnitId = unitId;
        let shouldFocus = false;
        const key = `${activeView}_${taskId}_${unitId}`;
        
        if (e.key === 'ArrowRight') {
            if (unitIndex < units.length - 1) {
                targetUnitId = units[unitIndex + 1].id;
                shouldFocus = true;
            } else if (taskIndex < leafTasksInOrder.length - 1) {
                targetTaskId = leafTasksInOrder[taskIndex + 1].id;
                targetUnitId = units[0].id;
                shouldFocus = true;
            }
            if (shouldFocus) e.preventDefault();
        } else if (e.key === 'ArrowLeft') {
            if (unitIndex > 0) {
                targetUnitId = units[unitIndex - 1].id;
                shouldFocus = true;
            } else if (taskIndex > 0) {
                targetTaskId = leafTasksInOrder[taskIndex - 1].id;
                targetUnitId = units[units.length - 1].id;
                shouldFocus = true;
            }
            if (shouldFocus) e.preventDefault();
        } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
            if (taskIndex < leafTasksInOrder.length - 1) {
                targetTaskId = leafTasksInOrder[taskIndex + 1].id;
                shouldFocus = true;
            }
            if (shouldFocus) e.preventDefault();
        } else if (e.key === 'ArrowUp') {
            if (taskIndex > 0) {
                targetTaskId = leafTasksInOrder[taskIndex - 1].id;
                shouldFocus = true;
            }
            if (shouldFocus) e.preventDefault();
        } else if (e.key === 'Tab') {
            if (e.shiftKey) {
                if (unitIndex > 0) {
                    targetUnitId = units[unitIndex - 1].id;
                    shouldFocus = true;
                } else if (taskIndex > 0) {
                    targetTaskId = leafTasksInOrder[taskIndex - 1].id;
                    targetUnitId = units[units.length - 1].id;
                    shouldFocus = true;
                }
            } else {
                if (unitIndex < units.length - 1) {
                    targetUnitId = units[unitIndex + 1].id;
                    shouldFocus = true;
                } else if (taskIndex < leafTasksInOrder.length - 1) {
                    targetTaskId = leafTasksInOrder[taskIndex + 1].id;
                    targetUnitId = units[0].id;
                    shouldFocus = true;
                }
            }
            if (shouldFocus) e.preventDefault();
        }
        
        if (edtMode === 'unit') {
            if (e.key === ' ' || e.key === 'Spacebar') {
                const currentValue = matrixData[key] || '-';
                const applies = isMatrixValueApplies(currentValue);
                const newValue = applies ? '-' : '1';
                setMatrixData(prev => syncMatrixPlanningToQuality(prev, taskId, unitId, newValue, activeView === 'planning'));
                e.preventDefault();
            } else if (e.key === '1' || e.key === '+') {
                setMatrixData(prev => syncMatrixPlanningToQuality(prev, taskId, unitId, '1', activeView === 'planning'));
                e.preventDefault();
            } else if (e.key === '0' || e.key === '-' || e.key === 'Delete' || e.key === 'Backspace') {
                setMatrixData(prev => syncMatrixPlanningToQuality(prev, taskId, unitId, '-', activeView === 'planning'));
                e.preventDefault();
            }
        }
        
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
            const val = matrixData[key] || '-';
            setCopiedValue(val);
            navigator.clipboard?.writeText(val).catch(() => {});
        }
        
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
            if (edtMode === 'unit') {
                const pasteVal = (val: string) => {
                    const applies = val !== undefined && val !== null && val !== '' && val !== '0' && val !== '-';
                    const newValue = applies ? '1' : '-';
                    setMatrixData(prev => syncMatrixPlanningToQuality(prev, taskId, unitId, newValue, activeView === 'planning'));
                };
                if (navigator.clipboard?.readText) {
                    navigator.clipboard.readText()
                        .then(text => pasteVal(text || copiedValue))
                        .catch(() => pasteVal(copiedValue));
                } else {
                    pasteVal(copiedValue);
                }
                e.preventDefault();
            }
        }

        if (shouldFocus) {
            const targetEl = document.getElementById(`cell-${targetTaskId}-${targetUnitId}`);
            if (targetEl) {
                targetEl.focus();
            }
        }
    };

    // Calculate Row Total for Payment View (Sum of all units for a task)
    const calculateRowPaymentTotal = (taskId: string) => {
        let total = 0;
        units.forEach(u => {
            const key = `payment_${taskId}_${u.id}`;
            const val = parseInt(matrixData[key] || '0', 10);
            if (!isNaN(val)) total += val;
            
            // Also sum anexos
            const anexosKey = `payment_anexos_${taskId}_${u.id}`;
            try {
                const anexos = JSON.parse(matrixData[anexosKey] || '[]');
                anexos.forEach((a: any) => {
                    total += (parseFloat(a.quantity) || 0) * (parseFloat(a.unitPrice) || 0);
                });
            } catch (e) {}
        });
        return total;
    };

    // --- Handlers ---

    const handleBudgetChange = (taskId: string, value: string) => {
        // Remove non-numeric characters for storage
        const numericValue = value.replace(/\D/g, '');
        setMatrixData(prev => ({
            ...prev,
            [`budget_total_${taskId}`]: numericValue
        }));
    };

    const handleTaskUnitChange = (taskId: string, unit: string) => {
        const updateRecursive = (list: Task[]): Task[] => {
            return list.map(t => {
                if (t.id === taskId) return { ...t, unit };
                if (t.children) return { ...t, children: updateRecursive(t.children) };
                return t;
            });
        };
        setTasks(prev => updateRecursive(prev));
    };

    const handleCellClick = (taskId: string, unitId: string, taskName: string, unitName: string, e?: React.MouseEvent) => {
        if (!isEditing) return; // Prevent edits if not in edit mode

        if (activeView === 'payment') {
            // Open Payment Detail Modal
            const qty = matrixData[`payment_qty_${taskId}_${unitId}`] || '';
            const unit = matrixData[`payment_unit_${taskId}_${unitId}`] || '';

            setEditingPaymentCoords({ taskId, unitId, taskName: cleanTaskName(taskName), unitName });
            setPaymentValues({ quantity: qty, unitPrice: unit });
            
            // Load Anexos from matrixData
            const anexosKey = `payment_anexos_${taskId}_${unitId}`;
            try {
                const existingAnexos = JSON.parse(matrixData[anexosKey] || '[]');
                setCellAnexos(existingAnexos);
            } catch (e) {
                setCellAnexos([]);
            }
            
            setPaymentModalOpen(true);
            return;
        }

        // --- Partial Fill Logic (Ctrl + Click) ---
        if (e?.ctrlKey && lastClickedCell && lastClickedCell.taskId === taskId && lastClickedCell.unitId !== unitId) {
            // Find all units in order
            const allVisibleUnitsInOrder: PhysicalUnit[] = [];
            levels.forEach(lvl => {
                units.filter(u => u.levelId === lvl.id).forEach(u => allVisibleUnitsInOrder.push(u));
            });

            const index1 = allVisibleUnitsInOrder.findIndex(u => u.id === lastClickedCell.unitId);
            const index2 = allVisibleUnitsInOrder.findIndex(u => u.id === unitId);

            if (index1 !== -1 && index2 !== -1) {
                const start = Math.min(index1, index2);
                const end = Math.max(index1, index2);
                const unitsToFill = allVisibleUnitsInOrder.slice(start, end + 1);

                const newEntries: Record<string, string> = {};
                unitsToFill.forEach(u => {
                    newEntries[`${activeView}_${taskId}_${u.id}`] = '1';
                    if (activeView === 'planning') {
                        newEntries[`quality_${taskId}_${u.id}`] = '1';
                    }
                });

                setMatrixData(prev => ({ ...prev, ...newEntries }));
                setLastClickedCell({ taskId, unitId });
                return;
            }
        }

        const key = `${activeView}_${taskId}_${unitId}`;
        const currentValue = matrixData[key] || '-';
        const newValue = currentValue === '-' ? '1' : '-';

        setMatrixData(prev => syncMatrixPlanningToQuality(prev, taskId, unitId, newValue, activeView === 'planning'));
        setLastClickedCell({ taskId, unitId });
    };

    const handleFillRowWithOnes = (taskId: string) => {
        if (!isEditing || activeView === 'payment') return;

        const task = findTaskByIdInTree(taskId, tasks);
        const leafTargets = (task && (task.type === 'folder' || (task.children && task.children.length > 0)))
            ? getTaskLeafChildren(task)
            : [task || { id: taskId }];

        const allFilled = units.every(u => 
            leafTargets.every(t => isMatrixValueApplies(matrixData[`${activeView}_${t.id}_${u.id}`]))
        );
        const newValue = allFilled ? '-' : '1';

        const newEntries: Record<string, string> = {};
        leafTargets.forEach(t => {
            units.forEach(u => {
                newEntries[`${activeView}_${t.id}_${u.id}`] = newValue;
                if (activeView === 'planning') {
                    newEntries[`quality_${t.id}_${u.id}`] = newValue;
                }
            });
        });

        setMatrixData(prev => ({
            ...prev,
            ...newEntries
        }));
    };

    const handleToggleApuSelection = (itemId: string, apuId: string) => {
        setApuData(prev => {
            const items = prev[itemId] || [];
            return {
                ...prev,
                [itemId]: items.map(a => a.id === apuId ? { ...a, selectedForEDT: !a.selectedForEDT } : a)
            };
        });
    };

    const handleDistributeBudget = (taskId: string) => {
        const newMatrixData = { ...matrixData };

        const linkedItems = budgetItems.filter(i => i.ganttTaskId === taskId);
        let taskBudgetTotal = 0;
        linkedItems.forEach(item => {
            const apus = apuData[item.id] || [];
            const selectedApusTotal = apus
                .filter(apu => apu.selectedForEDT !== false)
                .reduce((sum, apu) => sum + (apu.quantity * apu.unitPrice * item.quantity), 0);
            taskBudgetTotal += selectedApusTotal;
        });

        if (taskBudgetTotal > 0) {
            // Find active units for this task
            const activeUnits = units.filter(u => isMatrixValueApplies(matrixData[`planning_${taskId}_${u.id}`]));
            if (activeUnits.length > 0) {
                const distributedValue = Math.round(taskBudgetTotal / activeUnits.length);
                activeUnits.forEach(u => {
                    const key = `payment_${taskId}_${u.id}`;
                    newMatrixData[key] = distributedValue.toString();
                    newMatrixData[`payment_qty_${taskId}_${u.id}`] = "1";
                    newMatrixData[`payment_unit_${taskId}_${u.id}`] = distributedValue.toString();
                });
            } else {
                alert("No hay unidades físicas activas (en planificación) para esta partida.");
                return;
            }
        }

        setMatrixData(newMatrixData);
        alert("Presupuesto distribuido exitosamente para esta partida.");
    };

    const handleBudgetCellClick = (taskId: string) => {
        const hasLinkedItems = budgetItems.some(i => i.ganttTaskId === taskId);
        if (hasLinkedItems) {
            setActiveTaskId(taskId);
            setApuModalOpen(true);
        }
    };

    const handleSavePaymentDetails = () => {
        if (!editingPaymentCoords) return;
        const { taskId, unitId } = editingPaymentCoords;

        const q = parseFloat(paymentValues.quantity) || 0;
        const u = parseFloat(paymentValues.unitPrice) || 0;
        const total = Math.round(q * u);

        setMatrixData(prev => ({
            ...prev,
            [`payment_qty_${taskId}_${unitId}`]: paymentValues.quantity,
            [`payment_unit_${taskId}_${unitId}`]: paymentValues.unitPrice,
            [`payment_${taskId}_${unitId}`]: total.toString(),
            [`payment_anexos_${taskId}_${unitId}`]: JSON.stringify(cellAnexos)
        }));
        setPaymentModalOpen(false);
    };

    // Levels Handlers
    const handleAddLevelStart = () => {
        setIsCreatingLevel(true);
        setNewLevelName('');
    };

    const handleSaveNewLevel = () => {
        if (!newLevelName.trim()) return;
        if (editingLevelId) {
            // Edit existing
            setLevels(prev => prev.map(l => l.id === editingLevelId ? { ...l, name: newLevelName } : l));
            setEditingLevelId(null);
        } else {
            // Create new
            const newLevel: Level = {
                id: Math.random().toString(36).substr(2, 9),
                name: newLevelName
            };
            setLevels([...levels, newLevel]);
            addActivity({
                type: 'planning',
                title: 'Nivel agregado',
                description: `Se ha creado el nivel: ${newLevelName}`,
                user: 'Usuario' // Placeholder, in a real app this would come from auth
            });
        }
        setIsCreatingLevel(false);
        setNewLevelName('');
    };

    const handleDeleteLevel = (levelId: string) => {
        const hasUnits = units.some(u => u.levelId === levelId);
        if (hasUnits) {
            alert("No se puede eliminar el nivel porque tiene unidades físicas asociadas.");
            return;
        }
        if (confirm("¿Estás seguro de eliminar este nivel?")) {
            setLevels(prev => prev.filter(l => l.id !== levelId));
        }
    };

    const handleEditLevelStart = (level: Level) => {
        setEditingLevelId(level.id);
        setNewLevelName(level.name);
        setIsCreatingLevel(true);
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedLevelIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        // Required for Firefox
        e.dataTransfer.setData('text/plain', index.toString());
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedLevelIndex === null || draggedLevelIndex === index) return;

        const newLevels = [...levels];
        const draggedItem = newLevels[draggedLevelIndex];
        newLevels.splice(draggedLevelIndex, 1);
        newLevels.splice(index, 0, draggedItem);

        setDraggedLevelIndex(index);
        setLevels(newLevels);
    };

    const handleDragEnd = () => {
        setDraggedLevelIndex(null);
    };

    // Units Handlers
    const handleOpenCreateUnitForm = () => {
        setActiveModal('unit_create');
        // Auto-suggest next code based on last created unit
        let nextCode = '';
        if (units.length > 0) {
            const lastCode = units[units.length - 1].code;
            // Extract trailing numeric part and prefix
            const match = lastCode.match(/^(.*?)(\d+)$/);
            if (match) {
                const prefix = match[1];
                const num = parseInt(match[2], 10) + 1;
                const padded = String(num).padStart(match[2].length, '0');
                nextCode = prefix + padded;
            }
        }
        setNewUnitData({ code: nextCode, name: '', levelId: '' });
    };

    const handleSaveUnit = () => {
        if (!newUnitData.code || !newUnitData.name || !newUnitData.levelId) {
            alert("Por favor complete todos los campos");
            return;
        }

        if (editingUnitId) {
            // Edit existing
            setUnits(prev => prev.map(u => u.id === editingUnitId ? { id: u.id, ...newUnitData } : u));
            setEditingUnitId(null);
        } else {
            // Create new
            const newUnit: PhysicalUnit = {
                id: Math.random().toString(36).substr(2, 9),
                ...newUnitData
            };
            setUnits([...units, newUnit]);
            addActivity({
                type: 'planning',
                title: 'Unidad Física agregada',
                description: `Se ha creado la unidad: ${newUnitData.name} (${newUnitData.code})`,
                user: 'Usuario'
            });
        }
        setActiveModal('units_list');
    };

    const handleEditUnitStart = (unit: PhysicalUnit) => {
        setEditingUnitId(unit.id);
        setNewUnitData({ code: unit.code, name: unit.name, levelId: unit.levelId });
        setActiveModal('unit_create');
    };

    const handleDeleteUnit = (unitId: string) => {
        if (confirm("¿Estás seguro de eliminar esta unidad física?")) {
            setUnits(prev => prev.filter(u => u.id !== unitId));
            // Also clean up matrix data related to this unit
            setMatrixData(prev => {
                const newData = { ...prev };
                Object.keys(newData).forEach(key => {
                    if (key.endsWith(`_${unitId}`)) {
                        delete newData[key];
                    }
                });
                return newData;
            });
        }
    };

    const handleSaveMassiveUnits = () => {
        const { code, name, prefix, quantity, levelId } = massiveData;
        const qty = parseInt(quantity, 10);

        if (!code || !name || !prefix || !levelId || isNaN(qty) || qty <= 0) {
            alert("Por favor complete todos los campos y asegúrese de que la cantidad sea válida.");
            return;
        }

        const newUnits: PhysicalUnit[] = [];

        // Helper to increment numerical strings with padding
        const incrementString = (str: string, index: number) => {
            const numPart = str.match(/\d+/);
            if (!numPart) return `${str}${index}`; // Fallback if no numbers found

            const numStr = numPart[0];
            const numValue = parseInt(numStr, 10);
            const incrementedValue = numValue + index;

            // Maintain padding
            const paddedValue = incrementedValue.toString().padStart(numStr.length, '0');
            return str.replace(numStr, paddedValue);
        };

        const levelName = levels.find(l => l.id === levelId)?.name || '';

        for (let i = 0; i < qty; i++) {
            const unitCode = incrementString(code, i);
            const unitPrefix = incrementString(prefix, i);

            // Example naming logic: [Base Name] [Unit Prefix]
            // Casa 01 (Level and Code are separate columns)
            const unitName = `${name} ${unitPrefix}`.trim();

            newUnits.push({
                id: Math.random().toString(36).substr(2, 9),
                code: unitCode,
                name: unitName,
                levelId
            });
        }

        setUnits([...units, ...newUnits]);
        addActivity({
            type: 'planning',
            title: 'Unidades Físicas agregadas',
            description: `Se han creado ${newUnits.length} unidades físicas de forma masiva`,
            user: 'Usuario'
        });
        setActiveModal('units_list');
        setIsMassiveMode(false);
    };

    // Filtering
    const filteredLevels = levels.filter(l => l.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const filteredUnits = units.filter(u =>
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.code.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => {
        // Sort by level name first
        const levelA = levels.find(l => l.id === a.levelId)?.name || '';
        const levelB = levels.find(l => l.id === b.levelId)?.name || '';
        const levelCmp = levelA.localeCompare(levelB, 'es', { numeric: true });
        if (levelCmp !== 0) return levelCmp;
        // Then by code (natural numeric sort)
        return a.code.localeCompare(b.code, 'es', { numeric: true });
    });

    // --- Configuration Checks ---
    const hasTasks = tasks && tasks.length > 0;
    const hasLevels = levels && levels.length > 0;
    const hasUnits = units && units.length > 0;
    const isConfigured = hasTasks && hasLevels && hasUnits;

    // --- Recursive Task Renderer ---
    const renderTaskRows = (taskList: Task[], level = 0, rowCounter = { current: 0 }) => {
        let rows: React.ReactNode[] = [];

        taskList.filter(t => !t.isDeleted).forEach(task => {
            rowCounter.current++;
            const rowNum = rowCounter.current;
            const paddingLeft = level * 20 + 16; // Indentation logic
            const isFolder = task.type === 'folder' || (task.children && task.children.length > 0);
            const displayName = cleanTaskName(task.name);

            const itemIndexNumber = task.id_original_project !== undefined && task.id_original_project !== null ? task.id_original_project : rowNum;

            if (isFolder) {
                const leafSubtasks = getTaskLeafChildren(task);
                const folderTotal = calculateRowTotal(task.id);
                const isUnlocked = unlockedParentTaskId === task.id;

                rows.push(
                    <tr
                        key={task.id}
                        onDoubleClick={() => {
                            if (isEditing) {
                                setUnlockedParentTaskId(isUnlocked ? null : task.id);
                            }
                        }}
                        title={isUnlocked ? "Tarea Madre Desbloqueada - Ingrese valores y presione 'Desplegar'" : "Tarea Madre - Doble click para desbloquear línea y editar"}
                        className={`transition-all group ${
                            isUnlocked
                                ? 'bg-purple-50/90 dark:bg-purple-950/40 ring-2 ring-purple-500/70 z-20 font-bold'
                                : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200/70 dark:hover:bg-slate-700/70 cursor-pointer'
                        }`}
                    >
                        <td className="border border-slate-300 dark:border-slate-700 py-2 px-2 text-center text-xs font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 sticky left-0 z-10 w-[50px] min-w-[50px] max-w-[50px]">
                            {itemIndexNumber}
                        </td>
                        <td
                            className={`border border-slate-300 dark:border-slate-700 py-2 font-bold text-sm text-slate-800 dark:text-white sticky left-[50px] z-10 truncate ${
                                isUnlocked ? 'bg-purple-50 dark:bg-purple-950/60' : 'bg-slate-100 dark:bg-slate-800'
                            }`}
                            style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '16px', maxWidth: `${taskColumnWidth}px`, width: `${taskColumnWidth}px` }}
                            title={displayName}
                        >
                            <div className="flex items-center justify-between gap-2 w-full">
                                <span className="truncate flex-1" title={displayName}>{displayName}</span>
                                {isUnlocked ? (
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleDeployParentValues(task); }}
                                            title="Desplegar valores asignados a todas sus subtareas"
                                            className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-md shadow transition-all flex items-center gap-1 cursor-pointer animate-pulse"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">south</span>
                                            <span>Desplegar</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setUnlockedParentTaskId(null); }}
                                            title="Bloquear línea"
                                            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded transition-colors cursor-pointer"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">lock</span>
                                        </button>
                                    </div>
                                ) : isEditing ? (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setUnlockedParentTaskId(task.id); }}
                                        title="Doble click o clic aquí para desbloquear línea"
                                        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-purple-600 rounded transition-all shrink-0 cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">edit</span>
                                    </button>
                                ) : null}
                            </div>
                        </td>
                        <td className="border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-center text-xs font-semibold italic">
                            {task.type === 'folder' ? (
                                <span className="text-slate-400">Carpeta</span>
                            ) : (
                                <span className="text-purple-600 dark:text-purple-400 font-bold">Tarea Gantt</span>
                            )}
                        </td>
                        <td className="border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-right pr-3 text-xs font-bold font-mono text-slate-500 dark:text-slate-400">
                            {folderTotal}
                        </td>
                        {activeView === 'payment' && (
                            <>
                                <td className="border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-right text-xs font-mono text-slate-400 px-2 py-2">
                                    -
                                </td>
                                <td className="border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-right text-xs font-mono font-bold text-slate-500 px-2 py-2">
                                    -
                                </td>
                            </>
                        )}
                        {levels.map(level =>
                            getUnitsByLevel(level.id).map(unit => {
                                const totalKey = `${activeView}_${task.id}_${unit.id}`;
                                const value = matrixData[totalKey];
                                const parentVal = getParentTaskCellValue(task, unit.id);

                                if (isUnlocked) {
                                    return (
                                        <td
                                            key={totalKey}
                                            className="border border-purple-300 dark:border-purple-800 p-0 text-center text-sm w-[100px] min-w-[100px] max-w-[100px] bg-purple-50/50 dark:bg-purple-900/20"
                                        >
                                            <input
                                                id={`cell-${task.id}-${unit.id}`}
                                                type="text"
                                                readOnly={edtMode === 'unit'}
                                                value={
                                                    value !== undefined && value !== null && value !== '' && value !== '0' && value !== '-' ? value : ''
                                                }
                                                placeholder="-"
                                                onChange={(e) => {
                                                    if (edtMode === 'quantity') {
                                                        handleCellQuantityChange(task.id, unit.id, e.target.value);
                                                    }
                                                }}
                                                onKeyDown={(e) => handleCellKeyDown(task.id, unit.id, e)}
                                                onClick={() => handleCellInputClick(task.id, unit.id)}
                                                className={`w-full h-full bg-transparent border-0 outline-none text-center font-mono py-2 focus:bg-purple-100 dark:focus:bg-purple-900/50 text-purple-900 dark:text-purple-100 font-bold ${
                                                    edtMode === 'unit' ? 'cursor-pointer select-none' : ''
                                                }`}
                                            />
                                        </td>
                                    );
                                }

                                return (
                                    <td
                                        key={`${task.id}_${unit.id}`}
                                        onDoubleClick={() => isEditing && setUnlockedParentTaskId(task.id)}
                                        title="Tarea Madre - Doble click para desbloquear línea y editar"
                                        className="border border-slate-300 dark:border-slate-700 bg-slate-100/70 dark:bg-slate-800/70 text-center font-mono text-xs text-slate-400 dark:text-slate-500 font-semibold cursor-pointer select-none py-2 opacity-75"
                                    >
                                        {parentVal}
                                    </td>
                                );
                            })
                        )}
                    </tr>
                );
                if (task.children && task.children.length > 0) {
                    rows = [...rows, ...renderTaskRows(task.children, level + 1, rowCounter)];
                }
            } else {
                // Determine budget and accumulated total for Payment View
                const linkedItems = budgetItems.filter(i => i.ganttTaskId === task.id);
                let budgetTotal = 0;
                linkedItems.forEach(item => {
                    const apus = apuData[item.id] || [];
                    const selectedApusTotal = apus
                        .filter(apu => apu.selectedForEDT !== false)
                        .reduce((sum, apu) => sum + (apu.quantity * apu.unitPrice * item.quantity), 0);
                    budgetTotal += selectedApusTotal;
                });
                const accumulatedTotal = activeView === 'payment' ? calculateRowPaymentTotal(task.id) : 0;

                rows.push(
                    <tr key={task.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="border border-slate-300 dark:border-slate-700 py-1.5 px-2 text-center text-xs font-bold text-slate-500 dark:text-slate-400 bg-white dark:bg-[#1a2634] sticky left-0 z-10 w-[50px] min-w-[50px] max-w-[50px]">
                            {itemIndexNumber}
                        </td>
                        <td
                            className="border border-slate-300 dark:border-slate-700 py-1.5 text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-[#1a2634] sticky left-[50px] z-10 group"
                            style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '16px', maxWidth: `${taskColumnWidth}px`, width: `${taskColumnWidth}px` }}
                        >
                            <div className="flex items-center justify-between gap-2 w-full">
                                <span className="truncate flex-1" title={displayName}>{displayName}</span>
                                {isEditing && activeView !== 'payment' && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleFillRowWithOnes(task.id); }}
                                        title="Rellenar todas las unidades con '1'"
                                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 rounded-md transition-all flex items-center justify-center"
                                    >
                                        <span className="material-symbols-outlined !text-[18px]">playlist_add_check</span>
                                    </button>
                                )}
                            </div>
                        </td>
                        {/* Unidad Column */}
                        <td className="border border-slate-300 dark:border-slate-700 p-1 text-center bg-white dark:bg-[#1a2634]">
                            <select
                                disabled={!isEditing}
                                value={task.unit || ''}
                                onChange={(e) => handleTaskUnitChange(task.id, e.target.value)}
                                className={`w-full text-xs bg-transparent border-0 outline-none p-1 ${isEditing ? 'text-blue-600 font-bold hover:bg-blue-50' : 'text-slate-600 appearance-none'}`}
                            >
                                <option value="">-</option>
                                {CONSTRUCTION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </td>
                        {/* New Column: Total for Leaf Task */}
                        <td className="border border-slate-300 dark:border-slate-700 p-1 text-right font-bold text-xs bg-white dark:bg-[#1a2634] pr-3 font-mono">
                            {calculateRowTotal(task.id)}
                        </td>
                        {activeView === 'payment' && (
                            <>
                                {/* Presupuesto Column */}
                                <td
                                    className="border border-slate-300 dark:border-slate-700 p-0 text-center min-w-[100px] bg-white dark:bg-[#1a2634] cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                                    onClick={() => handleBudgetCellClick(task.id)}
                                >
                                    <div className="py-2 px-2 text-xs font-mono text-right text-slate-700 dark:text-slate-200">
                                        {budgetTotal > 0 ? formatCurrency(budgetTotal) : '-'}
                                    </div>
                                </td>
                                {/* EP Acumulado Column */}
                                <td className="border border-slate-300 dark:border-slate-700 p-0 text-center min-w-[100px] bg-slate-50/50 dark:bg-slate-800/20">
                                    <div className="py-2 px-2 text-xs font-mono text-right font-bold text-slate-800 dark:text-slate-100">
                                        {accumulatedTotal > 0 ? formatCurrency(accumulatedTotal) : '-'}
                                    </div>
                                </td>
                            </>
                        )}
                        {levels.map(level =>
                            getUnitsByLevel(level.id).map(unit => {
                                // Key includes activeView for rendering logic
                                const totalKey = `${activeView}_${task.id}_${unit.id}`;
                                const value = matrixData[totalKey];

                                return (
                                    <td
                                        key={totalKey}
                                        onClick={(e) => {
                                            if (isEditing && activeView === 'payment') {
                                                handleCellClick(task.id, unit.id, displayName, unit.name, e);
                                            }
                                        }}
                                        className={`
                                            border border-slate-300 dark:border-slate-700 p-0 text-center text-sm w-[100px] min-w-[100px] max-w-[100px]
                                            ${isEditing ? 'hover:bg-blue-50 dark:hover:bg-blue-900/20' : 'cursor-default'}
                                        `}
                                    >
                                        {activeView === 'payment' ? (
                                            <div className={`py-2 px-1 text-xs font-mono text-right w-full h-full flex items-center justify-end ${!value && isEditing ? 'text-slate-300' : 'text-slate-700 dark:text-slate-200'}`}>
                                                {(() => {
                                                    let cellTotal = value ? parseInt(value) : 0;
                                                    const anexosKey = `payment_anexos_${task.id}_${unit.id}`;
                                                    try {
                                                        const anexos = JSON.parse(matrixData[anexosKey] || '[]');
                                                        anexos.forEach((a: any) => {
                                                            cellTotal += (parseFloat(a.quantity) || 0) * (parseFloat(a.unitPrice) || 0);
                                                        });
                                                    } catch (e) {}
                                                    
                                                    return cellTotal > 0 ? formatCurrency(cellTotal) : (isEditing ? 'Click' : '-');
                                                })()}
                                            </div>
                                        ) : isEditing ? (
                                            <input
                                                id={`cell-${task.id}-${unit.id}`}
                                                type="text"
                                                readOnly={edtMode === 'unit'}
                                                value={
                                                    value !== undefined && value !== null && value !== '' && value !== '0' && value !== '-' ? value : ''
                                                }
                                                placeholder="-"
                                                onChange={(e) => {
                                                    if (edtMode === 'quantity') {
                                                        handleCellQuantityChange(task.id, unit.id, e.target.value);
                                                    }
                                                }}
                                                onKeyDown={(e) => handleCellKeyDown(task.id, unit.id, e)}
                                                onClick={() => handleCellInputClick(task.id, unit.id)}
                                                onFocus={(e) => {
                                                    setLastClickedCell({ taskId: task.id, unitId: unit.id });
                                                    e.target.select();
                                                }}
                                                className={`w-full h-full bg-transparent border-0 outline-none text-center font-mono py-2 focus:bg-blue-50 dark:focus:bg-blue-900/40 text-slate-800 dark:text-white ${
                                                    edtMode === 'unit' ? 'cursor-pointer select-none font-bold text-primary' : ''
                                                }`}
                                            />
                                        ) : (
                                            <div className={`py-2 font-mono ${
                                                (value !== undefined && value !== null && value !== '' && value !== '0' && value !== '-')
                                                    ? 'font-bold text-primary'
                                                    : 'text-slate-300 opacity-50'
                                            }`}>
                                                {value !== undefined && value !== null && value !== '' && value !== '0' && value !== '-'
                                                    ? value
                                                    : '-'}
                                            </div>
                                        )}
                                    </td>
                                );
                            })
                        )}
                    </tr>
                );

                // Recursively render children for tasks (subtasks)
                if (task.children && task.children.length > 0) {
                    rows = [...rows, ...renderTaskRows(task.children, level + 1, rowCounter)];
                }
            }
        });

        return rows;
    };

    // --- Modal Renderers ---

    // 1. Payment Detail Modal
    const renderPaymentModal = () => {
        const q = parseFloat(paymentValues.quantity) || 0;
        const u = parseFloat(paymentValues.unitPrice) || 0;
        const total = q * u;

        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-sm border border-slate-200 dark:border-slate-800 p-6 animate-in fade-in zoom-in duration-200">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-1">Detalle de Pago</h3>
                    <p className="text-xs text-slate-500 mb-4">{editingPaymentCoords?.taskName} • {editingPaymentCoords?.unitName}</p>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Cantidad</label>
                            <input
                                type="number"
                                autoFocus
                                className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                value={paymentValues.quantity}
                                onChange={(e) => setPaymentValues({ ...paymentValues, quantity: e.target.value })}
                                placeholder="0"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Valor Unitario</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2 text-slate-400">$</span>
                                <input
                                    type="number"
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 pl-6 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                    value={paymentValues.unitPrice}
                                    onChange={(e) => setPaymentValues({ ...paymentValues, unitPrice: e.target.value })}
                                    placeholder="0"
                                />
                            </div>
                        </div>

                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 mt-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Base:</span>
                                <span className="text-md font-bold text-slate-800">{formatCurrency(total)}</span>
                            </div>
                        </div>

                        {/* --- ANEXOS SECTION --- */}
                        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                             <div className="flex justify-between items-center mb-2">
                                <h4 className="text-xs font-bold text-slate-500 uppercase">Anexos de Celda</h4>
                                <button 
                                    onClick={() => {
                                        const newAnexo = { 
                                            id: Math.random().toString(36).substr(2, 9), 
                                            name: `Anexo ${cellAnexos.length + 1}`, 
                                            quantity: '0', 
                                            unitPrice: paymentValues.unitPrice || '0' 
                                        };
                                        setCellAnexos([...cellAnexos, newAnexo]);
                                    }}
                                    className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded border border-blue-200 hover:bg-blue-100 transition-colors"
                                >
                                    + Anexo
                                </button>
                             </div>
                             
                             <div className="space-y-3 max-h-[200px] overflow-y-auto pr-1">
                                {cellAnexos.map((anexo, index) => (
                                    <div key={anexo.id} className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded border border-slate-200 dark:border-slate-700 relative">
                                        <button 
                                            onClick={() => setCellAnexos(cellAnexos.filter(a => a.id !== anexo.id))}
                                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 shadow-sm hover:bg-red-600"
                                        >
                                            <span className="material-symbols-outlined text-[12px]">close</span>
                                        </button>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-[10px] text-slate-400 uppercase">Nombre</label>
                                                <input 
                                                    type="text" 
                                                    className="w-full text-xs border-b border-slate-300 bg-transparent py-0.5 outline-none focus:border-primary"
                                                    value={anexo.name}
                                                    onChange={e => {
                                                        const newArr = [...cellAnexos];
                                                        newArr[index].name = e.target.value;
                                                        setCellAnexos(newArr);
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] text-slate-400 uppercase">Precio</label>
                                                <input 
                                                    type="number" 
                                                    className="w-full text-xs border-b border-slate-300 bg-transparent py-0.5 outline-none focus:border-primary text-right"
                                                    value={anexo.unitPrice}
                                                    onChange={e => {
                                                        const newArr = [...cellAnexos];
                                                        newArr[index].unitPrice = e.target.value;
                                                        setCellAnexos(newArr);
                                                    }}
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-[10px] text-slate-400 uppercase">Cantidad</label>
                                                <input 
                                                    type="number" 
                                                    className="w-full text-xs border-b border-slate-300 bg-transparent py-0.5 outline-none focus:border-primary text-center font-bold"
                                                    value={anexo.quantity}
                                                    onChange={e => {
                                                        const newArr = [...cellAnexos];
                                                        newArr[index].quantity = e.target.value;
                                                        setCellAnexos(newArr);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {cellAnexos.length === 0 && (
                                    <p className="text-[10px] text-slate-400 italic text-center py-2">Sin anexos adicionales</p>
                                )}
                             </div>
                        </div>

                        <div className="pt-2 border-t-2 border-double border-slate-300 dark:border-slate-600 mt-2">
                            <div className="flex justify-between items-center text-primary">
                                <span className="text-sm font-bold uppercase">TOTAL GENERAL:</span>
                                <span className="text-lg font-black font-mono">
                                    {formatCurrency(total + cellAnexos.reduce((sum, a) => sum + (parseFloat(a.quantity) || 0) * (parseFloat(a.unitPrice) || 0), 0))}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button onClick={() => setPaymentModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 rounded text-sm font-medium">Cancelar</button>
                        <button onClick={handleSavePaymentDetails} className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 text-sm font-bold shadow-sm">Guardar</button>
                    </div>
                </div>
            </div>
        );
    };

    const renderLevelsModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">Gestionar Niveles</h3>
                    <button onClick={() => setActiveModal('none')} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                </div>
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <div className="relative">
                        <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
                        <input
                            type="text"
                            placeholder="Buscar niveles..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-primary outline-none"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {filteredLevels.length === 0 ? (
                        <p className="text-center text-slate-500 py-4 text-sm">No se encontraron niveles.</p>
                    ) : (
                        filteredLevels.map((level) => {
                            const absoluteIndex = levels.findIndex(l => l.id === level.id);
                            return (
                                <div
                                    key={level.id}
                                    draggable={searchQuery === ''}
                                    onDragStart={(e) => handleDragStart(e, absoluteIndex)}
                                    onDragOver={(e) => handleDragOver(e, absoluteIndex)}
                                    onDragEnd={handleDragEnd}
                                    className={`
                                        flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                                        ${draggedLevelIndex === absoluteIndex ? 'opacity-40 border-primary border-dashed border-2 shadow-inner' : 'hover:border-slate-300 dark:hover:border-slate-600'}
                                        ${searchQuery === '' ? 'cursor-move' : 'cursor-default'}
                                        transition-all duration-200
                                    `}
                                >
                                    <div className="flex items-center gap-3">
                                        {searchQuery === '' && (
                                            <span className="material-symbols-outlined text-slate-400 select-none">drag_indicator</span>
                                        )}
                                        <span className="font-medium text-slate-700 dark:text-slate-200">{level.name}</span>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => handleEditLevelStart(level)}
                                            className="text-slate-400 hover:text-primary transition-colors"
                                            title="Editar"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                        </button>
                                        {hasPermission('edt_niveles', 'edit') && (
                                            <button
                                                onClick={() => handleDeleteLevel(level.id)}
                                                className="text-slate-400 hover:text-red-500 transition-colors"
                                                title="Eliminar"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    {isCreatingLevel ? (
                        <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
                            <input
                                type="text"
                                autoFocus
                                placeholder="Nombre del nivel (Ej: Piso 1)"
                                className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-primary outline-none"
                                value={newLevelName}
                                onChange={(e) => setNewLevelName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveNewLevel()}
                            />
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setIsCreatingLevel(false)} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded">Cancelar</button>
                                <button onClick={handleSaveNewLevel} className="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-blue-600 rounded">Guardar</button>
                            </div>
                        </div>
                    ) : hasPermission('edt_niveles', 'edit') ? (
                        <button onClick={handleAddLevelStart} className="w-full py-2 bg-primary text-white rounded-lg font-medium text-sm hover:bg-blue-600 flex items-center justify-center gap-2">
                            <span className="material-symbols-outlined text-[18px]">add</span>
                            Agregar Nuevo Nivel
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );

    const renderUnitsListModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">Unidades Físicas</h3>
                    <div className="flex gap-2">
                        {hasPermission('edt_unidades', 'edit') && (
                            <button onClick={handleOpenCreateUnitForm} className="bg-primary text-white px-3 py-1.5 rounded text-sm flex items-center gap-1 hover:bg-blue-600"><span className="material-symbols-outlined text-[16px]">add</span>Crear</button>
                        )}
                        <button onClick={() => setActiveModal('none')} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                    </div>
                </div>
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <div className="relative">
                        <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
                        <input
                            type="text"
                            placeholder="Buscar unidades..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-primary outline-none"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-0">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                            <tr>
                                <th className="px-4 py-2 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200 dark:border-slate-700">Código</th>
                                <th className="px-4 py-2 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200 dark:border-slate-700">Nombre</th>
                                <th className="px-4 py-2 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200 dark:border-slate-700">Nivel</th>
                                <th className="px-4 py-2 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200 dark:border-slate-700 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {filteredUnits.length === 0 ? (
                                <tr><td colSpan={4} className="p-8 text-center text-slate-500">No se encontraron unidades físicas.</td></tr>
                            ) : (
                                filteredUnits.map(unit => {
                                    const unitLevelName = levels.find(l => l.id === unit.levelId)?.name || '-';
                                    return (
                                        <tr key={unit.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                                            <td className="px-4 py-3 text-sm font-mono text-slate-500">{unit.code}</td>
                                            <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-white">{unit.name}</td>
                                            <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                                                <span className="inline-block px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-xs">{unitLevelName}</span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEditUnitStart(unit)}
                                                        className="text-slate-400 hover:text-primary"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">edit</span>
                                                    </button>
                                                    {hasPermission('edt_unidades', 'edit') && (
                                                        <button
                                                            onClick={() => handleDeleteUnit(unit.id)}
                                                            className="text-slate-400 hover:text-red-500"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderCreateUnitFormModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                        {isMassiveMode ? 'Creación Masiva de Unidades' : (editingUnitId ? 'Editar Unidad Física' : 'Nueva Unidad Física')}
                    </h3>
                    <button
                        onClick={() => setIsMassiveMode(!isMassiveMode)}
                        className={`text-xs px-3 py-1.5 rounded-full font-bold transition-all ${isMassiveMode
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                            }`}
                    >
                        {isMassiveMode ? 'Cambiar a Simple' : 'Unidades Físicas Masivas'}
                    </button>
                </div>

                {isMassiveMode ? (
                    <div className="space-y-4 animate-in fade-in slide-in-from-right-2 duration-200">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Código Inicial</label>
                                <input
                                    type="text"
                                    placeholder="Ej: 001"
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary outline-none dark:bg-slate-800 dark:text-white"
                                    value={massiveData.code}
                                    onChange={e => setMassiveData({ ...massiveData, code: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Prefijo Inicial</label>
                                <input
                                    type="text"
                                    placeholder="Ej: 01"
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary outline-none dark:bg-slate-800 dark:text-white"
                                    value={massiveData.prefix}
                                    onChange={e => setMassiveData({ ...massiveData, prefix: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">Nombre Base</label>
                            <input
                                type="text"
                                placeholder="Ej: Casa"
                                className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary outline-none dark:bg-slate-800 dark:text-white"
                                value={massiveData.name}
                                onChange={e => setMassiveData({ ...massiveData, name: e.target.value })}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Cantidad</label>
                                <input
                                    type="number"
                                    placeholder="Ej: 10"
                                    min="1"
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary outline-none dark:bg-slate-800 dark:text-white"
                                    value={massiveData.quantity}
                                    onChange={e => setMassiveData({ ...massiveData, quantity: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Nivel / Agrupación</label>
                                <select
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary outline-none dark:bg-slate-800 dark:text-white"
                                    value={massiveData.levelId}
                                    onChange={e => setMassiveData({ ...massiveData, levelId: e.target.value })}
                                >
                                    <option value="">Seleccionar...</option>
                                    {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 animate-in fade-in slide-in-from-left-2 duration-200">
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">Código Identificador</label>
                            <input
                                type="text"
                                placeholder="Ej: 101"
                                className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary outline-none dark:bg-slate-800 dark:text-white"
                                value={newUnitData.code}
                                onChange={e => setNewUnitData({ ...newUnitData, code: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">Nombre Descriptivo</label>
                            <input
                                type="text"
                                placeholder="Ej: Departamento 101"
                                className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary outline-none dark:bg-slate-800 dark:text-white"
                                value={newUnitData.name}
                                onChange={e => setNewUnitData({ ...newUnitData, name: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">Nivel / Agrupación</label>
                            <select
                                className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm focus:ring-2 focus:ring-primary outline-none dark:bg-slate-800 dark:text-white"
                                value={newUnitData.levelId}
                                onChange={e => setNewUnitData({ ...newUnitData, levelId: e.target.value })}
                            >
                                <option value="">Seleccionar Nivel...</option>
                                {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                            {levels.length === 0 && <p className="text-xs text-amber-600 mt-1">⚠️ No hay niveles creados.</p>}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 mt-6">
                    <button
                        onClick={() => { setActiveModal('units_list'); setIsMassiveMode(false); setEditingUnitId(null); }}
                        className="px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 rounded text-sm font-medium"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={isMassiveMode ? handleSaveMassiveUnits : handleSaveUnit}
                        className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 text-sm font-medium shadow-sm transition-all active:scale-95"
                    >
                        {isMassiveMode ? `Generar ${massiveData.quantity} Unidades` : (editingUnitId ? 'Guardar Cambios' : 'Guardar Unidad')}
                    </button>
                </div>
            </div>
        </div>
    );

    // 2. APU Detail Modal
    const renderApuDetailModal = () => {
        if (!activeTaskId) return null;
        const linkedItems = budgetItems.filter(i => i.ganttTaskId === activeTaskId);
        const rawTaskName = tasks.find(t => t.id === activeTaskId)?.name || 'Tarea';
        const taskName = cleanTaskName(rawTaskName);

        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh] animate-in fade-in zoom-in duration-200">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Análisis de Precios Unitarios (APU)</h3>
                            <p className="text-xs text-slate-500 font-medium">Vinculado a: {taskName}</p>
                        </div>
                        <button onClick={() => setApuModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                    </div>

                    <div className="flex-1 overflow-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 shadow-sm z-10">
                                <tr>
                                    <th className="px-4 py-3 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-200 dark:border-slate-700 w-10"></th>
                                    <th className="px-4 py-3 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-200 dark:border-slate-700">Ítem / Recurso</th>
                                    <th className="px-4 py-3 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-200 dark:border-slate-700">Tipo</th>
                                    <th className="px-4 py-3 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-200 dark:border-slate-700">Unidad</th>
                                    <th className="px-4 py-3 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-200 dark:border-slate-700 text-right">Cant. APU</th>
                                    <th className="px-4 py-3 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-200 dark:border-slate-700 text-right">Valor Unit.</th>
                                    <th className="px-4 py-3 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-200 dark:border-slate-700 text-right">Cant. Global</th>
                                    <th className="px-4 py-3 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-200 dark:border-slate-700 text-right">Valor Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {linkedItems.length === 0 ? (
                                    <tr><td colSpan={7} className="p-8 text-center text-slate-400 italic">No hay ítems de presupuesto vinculados.</td></tr>
                                ) : (
                                    linkedItems.map(item => {
                                        const items = apuData[item.id] || [];
                                        return (
                                            <React.Fragment key={item.id}>
                                                <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                                                    <td colSpan={7} className="px-4 py-2 text-xs font-bold text-primary border-y border-slate-100 dark:border-slate-800">
                                                        Ítem: {item.code} - {item.description}
                                                    </td>
                                                </tr>
                                                {items.length === 0 ? (
                                                    <tr><td colSpan={7} className="px-8 py-3 text-xs text-slate-400 italic">Sin recursos asignados.</td></tr>
                                                ) : (
                                                    items.map(apu => {
                                                        const resource = budgetResources.find(r => r.id === apu.resourceId);
                                                        const globalQty = item.quantity;
                                                        const totalValue = apu.quantity * apu.unitPrice * globalQty;

                                                        return (
                                                            <tr key={apu.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${apu.selectedForEDT === false ? 'opacity-50' : ''}`}>
                                                                <td className="px-4 py-3 text-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={apu.selectedForEDT !== false}
                                                                        onChange={() => handleToggleApuSelection(item.id, apu.id)}
                                                                        className="rounded border-slate-300 text-primary focus:ring-primary"
                                                                    />
                                                                </td>
                                                                <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200 font-medium">{resource?.description || 'Recurso no encontrado'}</td>
                                                                <td className="px-4 py-3 text-xs text-slate-500">
                                                                    <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700">{resource?.type || '-'}</span>
                                                                </td>
                                                                <td className="px-4 py-3 text-xs text-slate-500">{resource?.unit || '-'}</td>
                                                                <td className="px-4 py-3 text-sm text-right font-mono text-slate-600 dark:text-slate-400">{apu.quantity.toLocaleString()}</td>
                                                                <td className="px-4 py-3 text-sm text-right font-mono text-slate-600 dark:text-slate-400">${apu.unitPrice.toLocaleString()}</td>
                                                                <td className="px-4 py-3 text-sm text-right font-mono text-slate-700 dark:text-slate-200">{globalQty.toLocaleString()}</td>
                                                                <td className="px-4 py-3 text-sm text-right font-mono font-bold text-primary">${totalValue.toLocaleString()}</td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                            <tfoot className="bg-slate-50 dark:bg-slate-800 font-bold sticky bottom-0 z-10 shadow-[0_-2px_4px_rgba(0,0,0,0.05)]">
                                <tr>
                                    <td colSpan={6} className="px-4 py-4 text-right text-slate-700 dark:text-slate-300">Presupuesto Total Consolidado (Seleccionado):</td>
                                    <td className="px-4 py-4 text-right text-primary text-lg">
                                        ${linkedItems.reduce((sum, item) => {
                                            const items = apuData[item.id] || [];
                                            return sum + items
                                                .filter(a => a.selectedForEDT !== false)
                                                .reduce((iSum, apu) => iSum + (apu.quantity * apu.unitPrice * item.quantity), 0);
                                        }, 0).toLocaleString()}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex justify-between items-center">
                        <button
                            onClick={() => handleDistributeBudget(activeTaskId)}
                            className="flex items-center gap-2 px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold shadow-md transition-all active:scale-95"
                        >
                            <span className="material-symbols-outlined text-[20px]">account_balance_wallet</span>
                            Distribución del presupuesto
                        </button>
                        <button onClick={() => setApuModalOpen(false)} className="px-8 py-2.5 bg-primary text-white rounded-lg font-bold hover:bg-blue-600 transition-colors shadow-sm">Cerrar Detalle</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-1 flex-col h-full overflow-hidden relative">
            {/* Header */}
            <header className="bg-[#1a73e8] dark:bg-blue-900 text-white px-6 h-14 flex items-center justify-between shadow-md z-30 shrink-0">
                <div className="flex items-center gap-6">
                    <h1 className="text-lg font-bold">EDT</h1>
                    <div className="relative">
                        <button
                            onClick={() => {
                                if (hasPermission('edt_unidades', 'view') || hasPermission('edt_niveles', 'view')) {
                                    setMenuOpen(!menuOpen);
                                }
                            }}
                            className={`flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded transition-colors ${menuOpen ? 'bg-white/20' : 'hover:bg-white/10'} ${!(hasPermission('edt_unidades', 'view') || hasPermission('edt_niveles', 'view')) ? 'opacity-50 pointer-events-none' : ''}`}
                        >
                            <span className="material-symbols-outlined text-[18px]">domain</span>
                            Unidades Físicas
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>

                        {menuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}></div>
                                <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in duration-100">
                                    <button
                                        onClick={() => { setMenuOpen(false); setActiveModal('units_list'); setSearchQuery(''); }}
                                        className={`w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ${!hasPermission('edt_unidades', 'edit') ? 'opacity-50 pointer-events-none' : ''}`}
                                    >
                                        Unidad Física
                                    </button>
                                    <button
                                        onClick={() => { setMenuOpen(false); setActiveModal('levels'); setSearchQuery(''); setIsCreatingLevel(false); }}
                                        className={`w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ${!hasPermission('edt_niveles', 'edit') ? 'opacity-50 pointer-events-none' : ''}`}
                                    >
                                        Gestión de Niveles
                                    </button>
                                    <button
                                        onClick={() => { setMenuOpen(false); setGraphicConfigOpen(true); }}
                                        className={`w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ${!hasPermission('conf_avance_grafico', 'view') ? 'opacity-50 pointer-events-none' : ''}`}
                                    >
                                        Configuración Avance Gráfico
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="text-xs text-blue-100">Proyecto Activo: {projectInfo.name}</div>
            </header>

            {/* View Selector & Edit Toolbar */}
            <div className="bg-white dark:bg-[#101822] border-b border-slate-200 dark:border-slate-700 px-6 py-2 flex items-center justify-between shrink-0 shadow-sm z-20">
                <div className="flex items-center gap-2">
                    {[
                        { id: 'planning', label: 'Planificación', icon: 'calendar_month' },
                        { id: 'quality', label: 'Control de Calidad', icon: 'fact_check' },
                        { id: 'payment', label: 'Estados de Pago', icon: 'payments' },
                    ].map(view => (
                        <button
                            key={view.id}
                            onClick={() => setActiveView(view.id as any)}
                            className={`
                                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                                ${activeView === view.id
                                    ? 'bg-primary text-white shadow-md shadow-blue-500/20'
                                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}
                            `}
                        >
                            <span className="material-symbols-outlined text-[18px]">{view.icon}</span>
                            {view.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    {activeView === 'planning' && isEditing && (
                        <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200 dark:border-slate-700 mr-1">
                            <button
                                onClick={() => setEdtMode('unit')}
                                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                                    edtMode === 'unit'
                                        ? 'bg-white dark:bg-slate-700 text-primary shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-800'
                                }`}
                            >
                                Unidad
                            </button>
                            <button
                                onClick={() => setEdtMode('quantity')}
                                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                                    edtMode === 'quantity'
                                        ? 'bg-white dark:bg-slate-700 text-primary shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-800'
                                }`}
                            >
                                Cubicación
                            </button>
                        </div>
                    )}
                    {/* Right Side: Edit Mode Button */}
                    <button
                        onClick={() => {
                            if (hasPermission('edt_editar', 'edit')) {
                                setIsEditing(!isEditing);
                            }
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${isEditing
                            ? 'bg-green-600 text-white hover:bg-green-700 shadow-md ring-2 ring-green-600 ring-offset-2 dark:ring-offset-slate-900'
                            : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'
                            } ${!hasPermission('edt_editar', 'edit') ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                        <span className="material-symbols-outlined text-[20px]">{isEditing ? 'save' : 'edit'}</span>
                        {isEditing ? 'Guardar Cambios' : 'Editar EDT'}
                    </button>
                </div>
            </div>

            {/* Matrix */}
            <main className="flex-1 overflow-auto bg-slate-50 dark:bg-background-dark">
                {isConfigured ? (
                    <div className="min-w-max p-6">
                        <table className="border-collapse border-spacing-0 bg-white dark:bg-[#1a2634] shadow-sm border border-slate-300 dark:border-slate-700">
                            <thead className="sticky top-0 z-20 shadow-sm">
                                <tr className="bg-white dark:bg-[#1a2634]">
                                    <th className="border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-2 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase w-[50px] min-w-[50px] max-w-[50px] sticky left-0 z-30" rowSpan={2}>
                                        #
                                    </th>
                                    <th
                                        className="border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-2 text-left italic font-medium text-slate-600 dark:text-slate-300 sticky left-[50px] z-30 flex items-center justify-between group"
                                        style={{ width: `${taskColumnWidth}px`, minWidth: `${taskColumnWidth}px` }}
                                    >
                                        <span>Tareas / Unidades Físicas</span>
                                        <div
                                            onMouseDown={handleMouseDown}
                                            className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 z-40 transition-colors ${isResizing ? 'bg-blue-500 w-2' : 'bg-transparent'}`}
                                        />
                                    </th>
                                    <th className="border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-2 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase w-[80px] min-w-[80px]" rowSpan={2}>
                                        Unidad
                                    </th>
                                    <th className="border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-2 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase w-[100px] min-w-[100px]" rowSpan={2}>
                                        Cantidad Total
                                    </th>
                                    {activeView === 'payment' && (
                                        <>
                                            <th className="border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-2 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase w-[100px] min-w-[100px]" rowSpan={2}>
                                                Presupuesto
                                            </th>
                                            <th className="border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-2 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase w-[100px] min-w-[100px]" rowSpan={2}>
                                                EP Acumulado
                                            </th>
                                        </>
                                    )}
                                    {levels.map(level => {
                                        const levelUnits = getUnitsByLevel(level.id);
                                        if (levelUnits.length === 0) return null;
                                        return (
                                            <th key={level.id} colSpan={levelUnits.length} className="border border-slate-300 dark:border-slate-700 border-t-4 border-t-green-600 text-center py-1 text-sm font-bold text-slate-800 dark:text-white bg-slate-50 dark:bg-slate-800 max-w-[200px]">
                                                <div className="line-clamp-2 break-words whitespace-normal px-1">{level.name}</div>
                                            </th>
                                        );
                                    })}
                                </tr>
                                <tr className="bg-white dark:bg-[#1a2634]">
                                    <th className="border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 sticky left-[50px] z-30"></th>
                                    {/* Offset for Unidad Column */}
                                    {/* levels header starts after Unidad */}
                                    {/* No new columns here, handled by rowSpan above */}
                                    {levels.map(level => {
                                        const levelUnits = getUnitsByLevel(level.id);
                                        return levelUnits.map(unit => (
                                            <th key={unit.id} className="border border-slate-300 dark:border-slate-700 px-1 py-1 text-xs text-center font-normal text-slate-600 dark:text-slate-300 w-[100px] min-w-[100px] max-w-[100px] bg-white dark:bg-[#1a2634]">
                                                <div className="flex flex-col items-center justify-start h-full">
                                                    <span className="text-[10px] text-slate-400 mb-0.5">{unit.code}</span>
                                                    <span className="font-medium line-clamp-3 break-words whitespace-normal leading-tight text-ellipsis overflow-hidden w-full px-0.5" title={unit.name}>{unit.name}</span>
                                                </div>
                                            </th>
                                        ));
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {renderTaskRows(tasks)}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-4">
                        <div className="size-24 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-2">
                            <span className="material-symbols-outlined text-5xl text-slate-400">warning</span>
                        </div>
                        <div className="text-center max-w-md">
                            <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Configuración Incompleta</h2>
                            <p className="text-slate-500 dark:text-slate-400 font-medium">
                                Cargar Planificación, Niveles y Unidades Fisicas para visualizar la EDT
                            </p>
                            {(!hasLevels || !hasUnits) && (
                                <button onClick={() => setActiveModal(hasLevels ? 'units_list' : 'levels')} className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors">
                                    {!hasLevels ? 'Configurar Niveles' : 'Configurar Unidades Físicas'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </main>

            {activeModal === 'levels' && renderLevelsModal()}
            {activeModal === 'units_list' && renderUnitsListModal()}
            {activeModal === 'unit_create' && renderCreateUnitFormModal()}
            {paymentModalOpen && renderPaymentModal()}
            {apuModalOpen && renderApuDetailModal()}
            
            {graphicConfigOpen && (
                <GraphicProgressConfig 
                    activeProjectId={projectInfo.id}
                    levels={levels}
                    units={units}
                    canEdit={hasPermission('conf_avance_grafico', 'edit')}
                    onClose={() => setGraphicConfigOpen(false)}
                />
            )}


        </div>
    );
};

export default EDTScreen;